import { useCallback, useState } from 'react';
import QrFullscreenViewer from './QrFullscreenViewer';
import { PAYMENT_QR_LABELS } from '../constants/bookingConst';
import { downloadPaymentQr } from '../utils/paymentQrUtil';
import '../css/PaymentQrDisplay.css';

/**
 * The selected payment QR on a checkout screen: the QR itself (tap it to
 * open it full screen) and a small "Download QR" button under it in the
 * club's Active Button Color. Shared by the court booking and Open Play
 * checkouts so both behave the same.
 *
 * @param {object} props
 * @param {{name: string, url: string}} props.method - the QR to show
 * @param {{entityType: string, entityId: string, performedBy?: string, summary?: object}|null} [props.audit]
 *   what a download is recorded against on the audit trail; null records nothing
 * @returns {JSX.Element|null}
 */
function PaymentQrDisplay({ method, audit = null }) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  /**
   * Saves the QR. When the browser can't read the image for a download
   * (a QR hosted somewhere that blocks it), the guest is told to long-press
   * instead — the image itself is always on screen to save that way.
   * @returns {Promise<void>}
   */
  const handleDownload = async () => {
    setError('');
    setDownloading(true);
    try {
      await downloadPaymentQr({ method, audit });
    } catch {
      setError(PAYMENT_QR_LABELS.DOWNLOAD_FAILED);
    } finally {
      setDownloading(false);
    }
  };

  // Stable so the viewer's Escape listener isn't re-attached on every render.
  const handleCloseViewer = useCallback(() => setViewerOpen(false), []);

  if (!method?.url) return null;

  return (
    <div className="pqr">
      <button
        type="button"
        className="cs-qr-card pqr-card"
        onClick={() => setViewerOpen(true)}
        aria-label={`${method.name} payment QR. ${PAYMENT_QR_LABELS.VIEW_HINT}`}
        title={PAYMENT_QR_LABELS.VIEW_HINT}
      >
        <img src={method.url} alt={method.name} />
      </button>
      <button
        type="button"
        className="pqr-download-btn"
        onClick={handleDownload}
        disabled={downloading}
      >
        {downloading ? PAYMENT_QR_LABELS.DOWNLOADING : PAYMENT_QR_LABELS.DOWNLOAD}
      </button>
      {error && !viewerOpen && <p className="pqr-error">{error}</p>}

      <QrFullscreenViewer
        isOpen={viewerOpen}
        method={method}
        onClose={handleCloseViewer}
        onDownload={handleDownload}
        downloading={downloading}
        error={error}
      />
    </div>
  );
}

export default PaymentQrDisplay;
